/* LEGEND — SUPPLY TIMELINE (wide panel over the chart). Ported from legend/supplytimeline.jsx.
   Reads the same server supply model as the Supply card (src/apps/realtime/src/supply/model.ts).
   One shared time axis, three bands:
     1. Shares — listed shares + public float from the daily supply rows (Jev corrections in),
        restated to today's basis; unverified stretches hatched; the days after the newest
        loaded row shaded "no data"
     2. Warrant overhang — log scale, against the float line
     3. Event lanes — splits · raises · warrants · registrations
   plus a summary strip and a filterable, linked event ledger. Opened from the Supply card. */
import React from 'react';
import { Icon } from './Widgets';
import { fmtMoneyShort } from '../lib/format';
import { DAY_MS, tsOf, fS, fD, fX, stepAt, inMask, stepSegments } from '../lib/supply';

const { useRef: useRefTL, useState: useStateTL, useEffect: useEffectTL, useMemo: useMemoTL } = React;
const h = React.createElement;

const CATS = [
  { k: 'split', label: 'Splits' },
  { k: 'raise', label: 'Raises' },
  { k: 'warrant', label: 'Warrants' },
  { k: 'reg', label: 'Registrations' },
];
const RANGES = [{ k: 'all', label: 'All' }, { k: '12', label: '12M' }, { k: '6', label: '6M' }];
const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function SupplyTimeline({ model: m, symbol, onClose }: { model: any; symbol: string; onClose: () => void }) {
  const wrapRef = useRefTL<any>(null), listRef = useRefTL<any>(null);
  const [W, setW] = useStateTL(800);
  const [range, setRange] = useStateTL('all');
  const [off, setOff] = useStateTL<Record<string, boolean>>({});
  const [sel, setSel] = useStateTL<number | null>(null);
  const [hover, setHover] = useStateTL<{ x: number; t: number } | null>(null);
  const [pos, setPos] = useStateTL<{ x: number; y: number } | null>(null);

  useEffectTL(() => {
    const el = wrapRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth)); ro.observe(el); setW(el.clientWidth);
    return () => ro.disconnect();
  }, [!!m]);
  useEffectTL(() => { setSel(null); }, [symbol]);

  const now = Date.now();
  const dom = useMemoTL(() => {
    if (!m) return null;
    // 'All' spans the loaded history plus events just before it; older ledger rows stay in the ledger.
    const start = Math.min(...['shares', 'float', 'overhang'].map((k) => (m.series[k][0] ? tsOf(m.series[k][0].date) : Infinity)), tsOf(m.dataThrough));
    const evs = m.events.map((e: any) => tsOf(e.date)).filter((t: number) => isFinite(t) && t >= start - 120 * DAY_MS);
    const lo = range === 'all' ? Math.min(start, ...evs) - 25 * DAY_MS : now - (range === '12' ? 365 : 183) * DAY_MS;
    return [lo, now + 18 * DAY_MS];
  }, [m, range]);

  if (!m || !dom) return null;
  const GL = 86, GR = 16, PW = Math.max(200, W - GL - GR);   // GL fits the 'Registrations' lane label
  const X = (t: number) => GL + (t - dom[0]) / (dom[1] - dom[0]) * PW;
  const XD = (d: string) => X(tsOf(d));
  const P1 = { y: 22, h: 114 }, P2 = { y: 170, h: 76 }, LN = { y: 266, h: 15 }, AX = LN.y + CATS.length * LN.h + 10, H = AX + 18;
  const endT = tsOf(m.dataThrough), endX = Math.min(XD(m.dataThrough), X(now));

  // ---- band 1: listed shares + float ----
  const listed = m.series.shares, float = m.series.float;
  const sVals = [...listed, ...float].filter((p: any) => p.v != null).map((p: any) => p.v);
  const sMax = Math.max(1, ...sVals) * 1.14;
  const Y1 = (v: number) => P1.y + P1.h - v / sMax * P1.h;
  const ticks1 = [0, 0.5, 1].map((f) => f * sMax / 1.14);
  const listedSegs = stepSegments(listed, XD, Y1, endX, GL);
  const floatSegs = stepSegments(float, XD, Y1, endX, GL);

  // ---- band 2: overhang (log) ----
  const oh = m.series.overhang;
  const floatOrd = m.float.ord || 0;
  const oMax = Math.max(10, ...oh.filter((p: any) => p.v != null).map((p: any) => p.v), floatOrd) * 2.2, oMin = 1000;
  const Y2 = (v: number) => { const c = Math.max(oMin, v || oMin); return P2.y + P2.h - (Math.log10(c) - Math.log10(oMin)) / (Math.log10(oMax) - Math.log10(oMin)) * P2.h; };
  const decades: number[] = []; for (let e = 3; Math.pow(10, e) <= oMax; e++) decades.push(Math.pow(10, e));
  const ohSegs = stepSegments(oh, XD, Y2, endX, GL);
  const ohNow = m.warrants.shares;
  const ohX = m.warrants.xFloat;

  // ---- axis ticks: months ----
  const ticks: number[] = [];
  const d0 = new Date(dom[0]); d0.setUTCDate(1); d0.setUTCMonth(d0.getUTCMonth() + 1);
  const spanMo = (dom[1] - dom[0]) / (30 * DAY_MS), every = spanMo > 20 ? 3 : spanMo > 9 ? 2 : 1;
  for (let d = new Date(d0); d.getTime() < dom[1]; d.setUTCMonth(d.getUTCMonth() + every)) ticks.push(d.getTime());

  // ---- events (filtered) ----
  const events = m.events.map((e: any, i: number) => ({ ...e, i })).filter((e: any) => !off[e.cat]);
  const inView = events.filter((e: any) => tsOf(e.date) >= dom[0] && tsOf(e.date) <= dom[1]);
  const laneOf = (c: string) => CATS.findIndex((x) => x.k === c);

  // ---- summary ----
  const lv = listed.filter((p: any) => p.v != null);
  const first = lv[0], last = lv[lv.length - 1];
  const growth = first && last ? (last.v / first.v - 1) * 100 : 0;
  const reverse = m.splits.filter((s: any) => s.factor < 1);
  const compound = reverse.reduce((a: number, s: any) => a * s.factor, 1);
  const raised = m.events.filter((e: any) => e.usd).reduce((a: number, e: any) => a + e.usd, 0);
  const pending = m.events.filter((e: any) => e.pending);

  function selectEv(i: number) {
    setSel(i);
    const row = listRef.current && listRef.current.querySelector('[data-ev="' + i + '"]');
    if (row) listRef.current.scrollTop = row.offsetTop - listRef.current.offsetTop - 30;
  }
  function onMove(e: any) {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) * (W / r.width);
    if (x < GL || x > GL + PW) { setHover(null); return; }
    setHover({ x, t: dom![0] + (x - GL) / PW * (dom![1] - dom![0]) });
  }
  function startDrag(e: any) {
    if (e.target.closest('button')) return;
    const el = e.currentTarget.parentElement, par = el.offsetParent || document.body;
    const r = el.getBoundingClientRect(), pr = par.getBoundingClientRect();
    const ox = r.left - pr.left, oy = r.top - pr.top, sx = e.clientX, sy = e.clientY;
    const mv = (ev: any) => setPos({ x: ox + ev.clientX - sx, y: oy + ev.clientY - sy });
    const up = () => { window.removeEventListener('pointermove', mv); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', mv); window.addEventListener('pointerup', up);
  }

  const ptTitle = (p: any) => [p.form ? p.form + (p.filed ? ' filed ' + fD(p.filed) : '') : fD(p.date), fS(p.v) + (p.raw != null ? ' (reported ' + p.raw.toLocaleString() + ')' : ''), p.fixed ? 'corrected' : null].filter(Boolean).join(' · ');

  const svg = h('svg', { className: 'stl-svg', width: W, height: H, viewBox: '0 0 ' + W + ' ' + H, onMouseMove: onMove, onMouseLeave: () => setHover(null) },
    h('defs', null,
      h('clipPath', { id: 'stl-clip' }, h('rect', { x: GL, y: 0, width: PW, height: H })),
      h('pattern', { id: 'stl-hatch', width: 6, height: 6, patternUnits: 'userSpaceOnUse', patternTransform: 'rotate(45)' },
        h('line', { x1: 0, y1: 0, x2: 0, y2: 6, className: 'stl-hatch-line' }))),
    // band labels + grid
    h('text', { x: GL, y: P1.y - 10, className: 'stl-band' }, 'SHARES', h('tspan', { className: 'stl-band-s', dx: 6 }, m.units === 'ADS' ? 'ADSs · split-adjusted' : 'split-adjusted')),
    ticks1.map((v, i) => h('g', { key: 'g1' + i },
      h('line', { x1: GL, x2: GL + PW, y1: Y1(v), y2: Y1(v), className: 'stl-grid' }),
      i ? h('text', { x: GL - 6, y: Y1(v) + 3, className: 'stl-ylab' }, fS(v)) : null)),
    h('text', { x: GL, y: P2.y - 12, className: 'stl-band' }, 'WARRANT OVERHANG', h('tspan', { className: 'stl-band-s', dx: 6 }, 'log scale')),
    decades.map((v, i) => h('g', { key: 'g2' + i },
      h('line', { x1: GL, x2: GL + PW, y1: Y2(v), y2: Y2(v), className: 'stl-grid' }),
      h('text', { x: GL - 6, y: Y2(v) + 3, className: 'stl-ylab' }, fS(v)))),
    CATS.map((c, i) => h('g', { key: c.k },
      h('line', { x1: GL, x2: GL + PW, y1: LN.y + i * LN.h, y2: LN.y + i * LN.h, className: 'stl-lane' }),
      h('text', { x: GL - 6, y: LN.y + i * LN.h + 3.5, className: 'stl-ylab lane ' + c.k + (off[c.k] ? ' off' : '') }, c.label))),
    ticks.map((t, i) => { const d = new Date(t); return h('g', { key: 'ax' + i },
      h('line', { x1: X(t), x2: X(t), y1: P1.y, y2: AX - 6, className: 'stl-vgrid' }),
      h('text', { x: X(t), y: AX + 8, className: 'stl-xlab' }, MONS[d.getUTCMonth()] + (d.getUTCMonth() < every ? " '" + String(d.getUTCFullYear()).slice(2) : ''))); }),
    h('g', { clipPath: 'url(#stl-clip)' },
      // unverified share-count stretches
      m.mask.map((s: any, i: number) => h('rect', { key: 'mk' + i, x: XD(s.from), y: P1.y - 4, width: Math.max(2, XD(s.to) - XD(s.from)), height: P1.h + 4, className: 'stl-mask' },
        h('title', null, 'Share count unverified ' + fD(s.from) + ' → ' + fD(s.to) + (s.reason ? ' · ' + s.reason : '')))),
      // nothing loaded after the newest row
      endT < now ? h('g', null,
        h('rect', { x: endX, y: P1.y - 4, width: Math.max(0, X(now) - endX), height: P2.y + P2.h - P1.y + 4, className: 'stl-nodata' }),
        // inside the span: along it when there is room, up its side when it is a few weeks wide
        X(now) - endX >= 150
          ? h('text', { x: (endX + X(now)) / 2, y: P1.y + P1.h - 4, textAnchor: 'middle', className: 'stl-nodata-lab' }, 'no data after ' + fD(m.dataThrough))
          : h('text', { x: 0, y: 0, textAnchor: 'middle', className: 'stl-nodata-lab', transform: 'translate(' + ((endX + X(now)) / 2 + 3) + ',' + (P1.y + P1.h * 0.7) + ') rotate(-90)' }, 'no data')) : null,
      // split verticals
      m.splits.map((s: any, i: number) => h('g', { key: 'sp' + i },
        h('line', { x1: XD(s.date), x2: XD(s.date), y1: P1.y - 4, y2: P2.y + P2.h, className: 'stl-splitline' }),
        h('text', { x: XD(s.date) + (GL + PW - XD(s.date) < 50 ? -4 : 4), y: P1.y + 6, textAnchor: GL + PW - XD(s.date) < 50 ? 'end' : 'start', className: 'stl-splitlab' }, s.ratio))),
      // today
      h('line', { x1: X(now), x2: X(now), y1: P1.y - 4, y2: AX - 6, className: 'stl-now' }),
      // shares
      listedSegs.map((s, i) => h('path', { key: 'la' + i, d: s.d + ' V' + (P1.y + P1.h) + ' H' + s.x0 + 'Z', className: 'stl-listed-area' })),
      listedSegs.map((s, i) => h('path', { key: 'll' + i, d: s.d, className: 'stl-listed' })),
      floatSegs.map((s, i) => h('path', { key: 'fl' + i, d: s.d, className: 'stl-floatline' })),
      listed.filter((p: any) => p.v != null).map((p: any, i: number) => h('circle', { key: 'pt' + i, cx: XD(p.date), cy: Y1(p.v), r: 3, className: 'stl-pt listed' + (p.fixed ? ' fixed' : '') }, h('title', null, 'Listed · ' + ptTitle(p)))),
      last ? h('text', { x: Math.min(endX - 4, XD(last.date) + 6), y: Y1(last.v) - 8, className: 'stl-ptlab', textAnchor: endX - XD(last.date) < 90 ? 'end' : 'start' }, fS(last.v)) : null,
      // overhang
      floatOrd ? h('line', { x1: GL, x2: GL + PW, y1: Y2(floatOrd), y2: Y2(floatOrd), className: 'stl-floatref' }) : null,
      floatOrd ? h('text', { x: GL + 4, y: Y2(floatOrd) - 4, className: 'stl-ptlab float' }, 'float ' + fS(floatOrd)) : null,
      ohSegs.map((s, i) => h('path', { key: 'oa' + i, d: s.d + ' V' + (P2.y + P2.h) + ' H' + s.x0 + 'Z', className: 'stl-oh-area' })),
      ohSegs.map((s, i) => h('path', { key: 'ol' + i, d: s.d, className: 'stl-oh' })),
      oh.filter((p: any) => p.v != null).map((p: any, i: number) => h('circle', { key: 'oh' + i, cx: XD(p.date), cy: Y2(p.v), r: 2.6, className: 'stl-pt oh' }, h('title', null, 'Warrants · ' + ptTitle(p)))),
      ohNow != null ? h('text', { x: endX - 4, y: Y2(ohNow) - 6, className: 'stl-ptlab oh', textAnchor: 'end' }, fS(ohNow) + (ohX != null ? ' · ' + fX(ohX) + ' float' : '')) : null,
      // event markers
      inView.map((e: any) => { const li = laneOf(e.cat), cy = LN.y + li * LN.h, cx = XD(e.date);
        return h('g', { key: 'ev' + e.i, className: 'stl-ev ' + e.cat + (e.pending ? ' pending' : '') + (sel === e.i ? ' sel' : ''), onClick: () => selectEv(e.i), transform: 'translate(' + cx + ',' + cy + ')' },
          sel === e.i ? h('line', { x1: 0, x2: 0, y1: P1.y - cy, y2: 0, className: 'stl-selline' }) : null,
          h('circle', { r: sel === e.i ? 5.5 : 4.2 }),
          h('title', null, fD(e.date) + ' — ' + e.title)); }),
      // hover crosshair
      hover ? h('line', { x1: hover.x, x2: hover.x, y1: P1.y - 4, y2: AX - 6, className: 'stl-cross' }) : null));

  let tip: any = null;
  if (hover) {
    const after = hover.t > endT + DAY_MS;
    const lsv = after ? null : stepAt(listed, hover.t), flv = after ? null : stepAt(float, hover.t), ov = after ? null : stepAt(oh, hover.t);
    tip = h('div', { className: 'stl-tip', style: { left: Math.min(W - 160, hover.x + 10) + 'px' } },
      h('b', null, fD(new Date(hover.t).toISOString())),
      after ? h('span', { className: 'tip-note' }, 'no data loaded') : null,
      lsv != null ? h('span', null, h('i', { className: 'lg listed' }), 'Listed ', h('em', null, fS(lsv))) : null,
      flv != null ? h('span', null, h('i', { className: 'lg float' }), 'Float ', h('em', null, fS(flv))) : null,
      ov != null ? h('span', null, h('i', { className: 'lg oh' }), 'Warrants ', h('em', null, fS(ov))) : null,
      !after && inMask(m.mask, hover.t) ? h('span', { className: 'tip-note' }, 'share count unverified') : null);
  }

  const sum = (k: string, v: string, sub: string, cls?: string) => h('div', { className: 'stl-sum', key: k }, h('span', { className: 'ss-k' }, k), h('span', { className: 'ss-v num ' + (cls || '') }, v), h('span', { className: 'ss-sub' }, sub));
  const summary = h('div', { className: 'stl-sums' },
    sum('Listed shares', first ? fS(first.v) + ' → ' + fS(last.v) : '—', first ? (growth >= 0 ? '+' : '') + Math.round(growth).toLocaleString() + '% since ' + fD(first.date) : '', growth > 100 ? 'down' : ''),
    sum('Reverse splits', String(reverse.length), reverse.length ? 'compound 1-for-' + Math.round(1 / compound).toLocaleString() : 'none', reverse.length >= 2 ? 'warn' : ''),
    sum('Raised', raised ? fmtMoneyShort(raised) : '—', m.events.filter((e: any) => e.cat === 'raise').length + ' financings'),
    sum('Overhang now', fS(ohNow), ohX != null ? fX(ohX) + ' float' : '—', ohX != null && ohX >= 1 ? 'down' : ohX != null && ohX >= 0.5 ? 'warn' : ''),
    pending.length ? sum('Pending', pending.length + ' flag' + (pending.length > 1 ? 's' : ''), pending[0].title.replace(' — not applied', ''), 'warn') : null);

  const ledger = h('div', { className: 'stl-ledger' },
    h('div', { className: 'stl-lhead' },
      h('span', { className: 'stl-lt' }, 'Event ledger'),
      h('div', { className: 'stl-cats' }, CATS.map((c) => h('button', { key: c.k, className: 'stl-cat ' + c.k + (off[c.k] ? ' off' : ''), onClick: () => setOff((o) => ({ ...o, [c.k]: !o[c.k] })) },
        h('i', null), c.label, h('b', null, m.events.filter((e: any) => e.cat === c.k).length))))),
    h('div', { className: 'stl-rows', ref: listRef }, events.slice().reverse().map((e: any) => h('div', {
        key: e.i, 'data-ev': e.i, className: 'stl-row ' + e.cat + (sel === e.i ? ' sel' : '') + (e.pending ? ' pending' : ''), onClick: () => setSel(e.i) },
      h('span', { className: 'sr-date num' }, fD(e.date)),
      h('span', { className: 'sr-cat' }, h('i', null), CATS[laneOf(e.cat)].label.replace(/s$/, '')),
      h('div', { className: 'sr-main' }, h('span', { className: 'sr-title' }, e.title), e.detail ? h('span', { className: 'sr-detail' }, e.detail) : null),
      h('span', { className: 'sr-imp num' }, e.impact ? '+' + fS(e.impact) : ''),
      e.url ? h('a', { className: 'sr-link', href: e.url, target: '_blank', rel: 'noreferrer', onClick: (ev: any) => ev.stopPropagation(), title: 'Open filing on SEC EDGAR' }, '↗') : h('span', null)))));

  const style = pos ? { left: pos.x + 'px', top: pos.y + 'px', transform: 'none' } : undefined;
  return h('div', { className: 'stl-panel', style },
    h('div', { className: 'stl-head', onPointerDown: startDrag },
      h('span', { className: 'pc-sym' }, symbol),
      h('span', { className: 'stl-title' }, 'Float & dilution timeline'),
      h('span', { className: 'stl-name' }, m.name),
      h('div', { className: 'stl-ranges' }, RANGES.map((r) => h('button', { key: r.k, className: range === r.k ? 'on' : '', onClick: () => setRange(r.k) }, r.label))),
      h('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close timeline' }, Icon.x())),
    h('div', { className: 'stl-scroll' }, summary,
      h('div', { className: 'stl-chart', ref: wrapRef }, svg, tip,
        h('div', { className: 'stl-legend' },
          h('span', null, h('i', { className: 'lg listed' }), m.units === 'ADS' ? 'ADSs outstanding' : 'Listed shares'),
          h('span', null, h('i', { className: 'lg floatline' }), 'Public float'),
          h('span', null, h('i', { className: 'lg oh' }), 'Warrant overhang'),
          h('span', null, h('i', { className: 'lg split' }), 'Reverse split'),
          m.mask.length ? h('span', null, h('i', { className: 'lg mask' }), 'Unverified count') : null,
          h('span', null, h('i', { className: 'lg nodata' }), 'No data loaded'))),
      ledger),
    h('div', { className: 'stl-foot' },
      'Counts restated to today’s share basis · data to ' + fD(m.dataThrough),
      m.run.tag ? ' · ' + m.run.tag : '', m.run.reader ? ' · ' + m.run.reader : '', m.reconStatus ? ' · reconciliation ' + String(m.reconStatus).toLowerCase() : ''));
}
